import { useCallback, useEffect, useRef, useState } from 'react';

interface UsePushNavigationOptions<V extends string> {
  initial: V;
  /**
   * Abilita la gesture "swipe-from-left" tipica di iOS/Android per tornare
   * alla view precedente. Default: true.
   */
  enableSwipeBack?: boolean;
  /**
   * Larghezza (px) dal bordo sinistro in cui la gesture viene riconosciuta.
   * Default: 24px (~match iOS hit area).
   */
  edgeZonePx?: number;
  /**
   * Distanza orizzontale (px) oltre cui lo swipe è considerato un "pop".
   * Default: 60px.
   */
  threshold?: number;
}

interface UsePushNavigationApi<V extends string> {
  view: V;
  stack: V[];
  canPop: boolean;
  push: (view: V) => void;
  pop: () => void;
  reset: (view?: V) => void;
}

/**
 * Hook di navigazione master/detail per shell mobile (chat, gruppi, canali).
 *
 * Mantiene uno stack in-memory + sincronizza la cronologia browser così:
 * - il tasto "indietro" del browser/Android e lo swipe-from-left mobile
 *   chiamano `pop()` invece di abbandonare la pagina;
 * - quando l'utente è già sulla root (`canPop=false`) il back nativo viene
 *   lasciato proseguire.
 *
 * Le view sono identificatori opachi tipizzati a livello chiamante (es.
 * `'list' | 'thread' | 'composer'`), così il consumer ha typing stretto
 * senza che il hook conosca la semantica.
 */
export function usePushNavigation<V extends string>(
  options: UsePushNavigationOptions<V>,
): UsePushNavigationApi<V> {
  const { initial, enableSwipeBack = true, edgeZonePx = 24, threshold = 60 } = options;
  const [stack, setStack] = useState<V[]>([initial]);
  const stackRef = useRef<V[]>([initial]);
  const popInternalRef = useRef(false);

  const sync = useCallback((next: V[]) => {
    stackRef.current = next;
    setStack(next);
  }, []);

  const push = useCallback(
    (view: V) => {
      const next = [...stackRef.current, view];
      sync(next);
      try {
        window.history.pushState({ __pushNav: true, depth: next.length }, '');
      } catch {
        // Ignore (es. ambienti senza history mutation).
      }
    },
    [sync],
  );

  const pop = useCallback(() => {
    if (stackRef.current.length <= 1) return;
    popInternalRef.current = true;
    try {
      window.history.back();
    } catch {
      // Fallback in-memory: rimuovi top senza toccare la history.
      const next = stackRef.current.slice(0, -1);
      sync(next);
      popInternalRef.current = false;
    }
  }, [sync]);

  const reset = useCallback(
    (view?: V) => {
      const root = view ?? stackRef.current[0] ?? initial;
      sync([root]);
    },
    [initial, sync],
  );

  // Listener back-button browser/Android. Quando lo stack ha profondità > 1
  // intercettiamo l'evento e rimuoviamo solo l'ultima view.
  useEffect(() => {
    const onPop = () => {
      if (stackRef.current.length > 1) {
        const next = stackRef.current.slice(0, -1);
        sync(next);
        if (!popInternalRef.current) {
          // Back nativo (utente, non noi): la history è già scesa di 1, ok.
        }
      }
      popInternalRef.current = false;
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [sync]);

  // Gesture swipe-from-left.
  useEffect(() => {
    if (!enableSwipeBack) return;
    let startX = 0;
    let startY = 0;
    let tracking = false;

    const onTouchStart = (e: TouchEvent) => {
      if (stackRef.current.length <= 1) return;
      const t = e.touches[0];
      if (!t) return;
      if (t.clientX > edgeZonePx) return;
      startX = t.clientX;
      startY = t.clientY;
      tracking = true;
    };
    const onTouchMove = (e: TouchEvent) => {
      if (!tracking) return;
      const t = e.touches[0];
      if (!t) return;
      const dx = t.clientX - startX;
      const dy = Math.abs(t.clientY - startY);
      if (dy > 40 && dy > dx) {
        tracking = false; // gesture verticale → annulla
      }
    };
    const onTouchEnd = (e: TouchEvent) => {
      if (!tracking) return;
      tracking = false;
      const t = e.changedTouches[0];
      if (!t) return;
      const dx = t.clientX - startX;
      if (dx >= threshold) {
        pop();
      }
    };

    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', onTouchEnd, { passive: true });
    window.addEventListener('touchcancel', onTouchEnd, { passive: true });
    return () => {
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [enableSwipeBack, edgeZonePx, threshold, pop]);

  return {
    view: stack[stack.length - 1] ?? initial,
    stack,
    canPop: stack.length > 1,
    push,
    pop,
    reset,
  };
}
